import { useQuery } from '@tanstack/react-query';
import { api } from '@/client/api';
import { Button, Card } from '@/client/components/ui';

/**
 * The sign-in screen. Also the place a misconfigured deployment finds out it
 * is misconfigured, before anyone gets a confusing OAuth error from Google.
 */
export function SignIn() {
  const health = useQuery({ queryKey: ['health'], queryFn: api.health, retry: false });
  const params = new URLSearchParams(window.location.search);
  const authError = params.get('auth_error');
  const missing = health.data?.missing ?? [];

  return (
    <main className="flex min-h-dvh items-center justify-center px-5 py-10">
      <Card className="w-full max-w-md p-6">
        <img src="/logo.svg" alt="" className="mx-auto mb-4 h-28 w-auto" />
        <h1 className="text-xl font-semibold tracking-tight">Kingdom Haul</h1>
        <p className="mt-2 text-sm text-ink-muted">
          Coordinate volunteer drivers for Thursday and Sunday meetings. Your data lives in your own
          Google Drive — this app never stores a copy.
        </p>

        {authError ? (
          <p className="mt-4 rounded-xl border border-critical/30 bg-critical-soft px-4 py-3 text-sm">
            {authError}
          </p>
        ) : null}

        {missing.length > 0 ? (
          <div className="mt-4 rounded-xl border border-caution/40 bg-caution-soft px-4 py-3 text-sm">
            <p className="font-medium">This deployment is not configured yet.</p>
            <p className="mt-1 text-ink-muted">
              Missing secrets: <code className="font-mono text-xs">{missing.join(', ')}</code>. See
              the README for the one-time setup.
            </p>
          </div>
        ) : null}

        <Button
          variant="primary"
          fullWidth
          className="mt-6"
          disabled={missing.length > 0}
          onClick={() => {
            window.location.href = '/auth/login';
          }}
        >
          <GoogleMark />
          Sign in with Google
        </Button>

        <p className="mt-4 text-xs text-ink-faint">
          The app asks only for permission to manage the one spreadsheet and the calendars it
          creates. It cannot see anything else in your Drive or Calendar.{' '}
          <a href="/privacy.html" className="underline underline-offset-2 hover:text-ink-muted">
            Privacy policy
          </a>{' '}
          ·{' '}
          <a href="/terms.html" className="underline underline-offset-2 hover:text-ink-muted">
            Terms
          </a>
        </p>
      </Card>
    </main>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 18 18" className="size-4" aria-hidden="true">
      <path
        fill="currentColor"
        d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="currentColor"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 009 18z"
        opacity=".75"
      />
      <path
        fill="currentColor"
        d="M3.97 10.72a5.4 5.4 0 010-3.44V4.95H.96a9 9 0 000 8.1l3.01-2.33z"
        opacity=".5"
      />
      <path
        fill="currentColor"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 00.96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
        opacity=".9"
      />
    </svg>
  );
}
